import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../../lib/api.js'
import { Card, ErrorBox, Field, Page, Spinner, SubmitButton, useAsync, useConfirm, useToast } from '../../components/Ui.jsx'

const BLANK = {
  code: '', name: '', ownerName: '', phone: '', email: '', caaRegistrationNo: '',
  address: '', village: '', mandal: '', district: 'Kakinada',
  lat: '', lng: '', annualCapacityCr: '', broodstockPartners: '', biosecurityNotes: '',
  username: '', password: ''
}

// Common landing points in the belt, so nobody has to hunt for coordinates
// during onboarding.
const PRESETS = [
  { label: 'Kakinada town', lat: 16.9891, lng: 82.2475 },
  { label: 'Vakalapudi', lat: 17.0180, lng: 82.2560 },
  { label: 'Uppada', lat: 17.0800, lng: 82.3300 },
  { label: 'U. Kothapalli', lat: 17.1250, lng: 82.3450 },
  { label: 'Mulapeta', lat: 17.2400, lng: 82.4700 },
  { label: 'Tuni', lat: 17.3500, lng: 82.5500 }
]

export default function HatcheryForm () {
  const { id } = useParams()
  const editing = Boolean(id)
  const navigate = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()

  const [form, setForm] = useState(BLANK)
  const [busy, setBusy] = useState(false)

  const existing = useAsync(
    () => (editing ? api.get(`/admin/hatcheries/${id}`) : Promise.resolve(null)),
    [id]
  )
  const nextCode = useAsync(
    () => (editing ? Promise.resolve(null) : api.get('/admin/hatcheries/next-code')),
    [editing]
  )

  useEffect(() => {
    if (existing.data?.hatchery) {
      const h = existing.data.hatchery
      setForm(f => ({
        ...BLANK,
        ...Object.fromEntries(Object.keys(BLANK).map(k => [k, h[k] ?? ''])),
        code: h.code
      }))
    }
  }, [existing.data])

  useEffect(() => {
    if (nextCode.data?.code) setForm(f => (f.code ? f : { ...f, code: nextCode.data.code }))
  }, [nextCode.data])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: editing ? 'Save this hatchery profile?' : 'Create this hatchery?',
      body: editing
        ? 'The desk and the public directory both show the new details straight away. Moving the coordinates changes every distance calculation for this hatchery.'
        : 'The hatchery record is created and a portal login is issued with it.',
      facts: [['Hatchery', form.name], ['Code', form.code], ['Coordinates', `${form.lat}, ${form.lng}`]],
      confirmLabel: editing ? 'Save changes' : 'Create hatchery'
    })
    if (!ok) return
    setBusy(true)
    try {
      if (editing) {
        await api.patch(`/admin/hatcheries/${id}`, form)
        toast.ok('Hatchery profile updated.')
        navigate(`/admin/hatcheries/${id}`)
      } else {
        const res = await api.post('/admin/hatcheries', form)
        toast.ok(`Hatchery onboarded. Login issued: ${res.credentials.username} / ${res.credentials.password}`)
        navigate(`/admin/hatcheries/${res.hatchery._id}?issued=1`)
      }
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (editing && existing.loading) return <Page title="Edit hatchery"><Spinner /></Page>
  if (editing && existing.error) return <Page title="Edit hatchery"><ErrorBox error={existing.error} onRetry={existing.reload} /></Page>

  return (
    <Page
      crumb={<Link to="/admin/hatcheries">← Hatcheries</Link>}
      title={editing ? `Edit ${form.name}` : 'Onboard a hatchery'}
      subtitle={editing
        ? 'Update the profile the operator desk sees when it pitches this hatchery to a farmer.'
        : 'Create the hatchery record and issue its portal login. The username and password are shown once here and stay visible on the hatchery page so you can pass them on by phone.'}
    >
      <form onSubmit={submit}>
        <Card title="Identity & registration">
          <div className="formgrid">
            <Field label="Hatchery code" required hint="Auto-allocated; change it only if the hatchery has its own code.">
              <input value={form.code} onChange={e => set('code', e.target.value)} required />
            </Field>
            <Field label="Hatchery name" required>
              <input value={form.name} onChange={e => set('name', e.target.value)} required />
            </Field>
            <Field label="Owner / contact person">
              <input value={form.ownerName} onChange={e => set('ownerName', e.target.value)} />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 …" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </Field>
            <Field label="CAA registration number" hint="Coastal Aquaculture Authority registration, shown on every batch.">
              <input value={form.caaRegistrationNo} onChange={e => set('caaRegistrationNo', e.target.value)} placeholder="AP/CAA/2026/…" />
            </Field>
          </div>
        </Card>

        <Card title="Location — used for haul distance">
          <div className="formgrid">
            <Field label="Village">
              <input value={form.village} onChange={e => set('village', e.target.value)} />
            </Field>
            <Field label="Mandal">
              <input value={form.mandal} onChange={e => set('mandal', e.target.value)} />
            </Field>
            <Field label="District">
              <input value={form.district} onChange={e => set('district', e.target.value)} />
            </Field>
            <Field label="Latitude" required>
              <input type="number" step="0.0001" value={form.lat} onChange={e => set('lat', e.target.value)} required />
            </Field>
            <Field label="Longitude" required>
              <input type="number" step="0.0001" value={form.lng} onChange={e => set('lng', e.target.value)} required />
            </Field>
            <Field label="Address" wide>
              <input value={form.address} onChange={e => set('address', e.target.value)} />
            </Field>
          </div>
          <div className="chips">
            <span className="muted small" style={{ alignSelf: 'center', marginRight: 4 }}>Quick fill:</span>
            {PRESETS.map(p => (
              <button type="button" key={p.label} className="chip"
                onClick={() => setForm(f => ({ ...f, lat: p.lat, lng: p.lng, village: f.village || p.label }))}>
                {p.label}
              </button>
            ))}
          </div>
        </Card>

        <Card title="Capability">
          <div className="formgrid">
            <Field label="Annual capacity (crore PL)">
              <input type="number" step="0.1" value={form.annualCapacityCr} onChange={e => set('annualCapacityCr', e.target.value)} />
            </Field>
            <Field label="Broodstock partners" hint="Kona Bay, SIS, Benchmark, local multiplication centre…">
              <input value={form.broodstockPartners} onChange={e => set('broodstockPartners', e.target.value)} />
            </Field>
            <Field label="Biosecurity notes" wide>
              <textarea value={form.biosecurityNotes} onChange={e => set('biosecurityNotes', e.target.value)}
                placeholder="Filtration, effluent handling, screening routine — what the operator can tell a farmer." />
            </Field>
          </div>
        </Card>

        {!editing && (
          <Card title="Portal login">
            <p className="muted small" style={{ marginTop: 0 }}>
              Leave both blank and the platform generates them. Either way the credentials are displayed
              after saving and remain readable on the hatchery page until the hatchery changes the password itself.
            </p>
            <div className="formgrid">
              <Field label="Username" hint="Blank = derived from the hatchery name.">
                <input value={form.username} onChange={e => set('username', e.target.value)} autoComplete="off" />
              </Field>
              <Field label="Password" hint="Blank = a readable one is generated, e.g. Tide@4417.">
                <input value={form.password} onChange={e => set('password', e.target.value)} autoComplete="off" />
              </Field>
            </div>
          </Card>
        )}

        <div className="btnrow">
          <SubmitButton busy={busy} type="submit">
            {editing ? 'Save changes' : 'Onboard hatchery & issue login'}
          </SubmitButton>
          <Link className="btn" to={editing ? `/admin/hatcheries/${id}` : '/admin/hatcheries'}>Cancel</Link>
        </div>
      </form>
    </Page>
  )
}
